import client from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

export const uploadFile = ({
  payload,
  allowImageBasedPdf = false,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/common/upload`,
      method: RequestMethod.POST,
      params: { allowImageBasedPdf },
      data: payload,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getInitiativePageDetails = ({ pageName, version = 'v1' }) =>
  client
    .request({
      url: `${version}/common/initiative-pages/${pageName}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFeatureOnboardingList = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/common/feature-onboarding/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const completeFeatureOnboarding = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/common/feature-onboarding/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Returns error response if the random number is 5.
 * The range of the random number is [0; 9]
 */
export const mockApiRequest = (
  resData = 1,
  errorMsg = 'Request failed!',
  milliseconds = 500,
) => {
  const randomInt = Math.floor(Math.random() * 10);
  return new Promise((resolve, reject) => {
    setTimeout(
      () => (randomInt !== 5 ? resolve(resData) : reject(errorMsg)),
      milliseconds,
    );
  });
};

export const validateZipCode = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/common/validate-zip/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAutocompleteList = ({ params, version = 'v1' }) => {
  const searchTerm = params?.term || params?.search || '';
  return client
    .request({
      url: `${version}/common/autocomplete/`,
      method: RequestMethod.GET,
      params: {
        ...params,
        term: searchTerm,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

export const createPage = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/common/pages/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createAlternatePage = ({ version = 'v1', payload, slug }) =>
  client
    .request({
      url: `${version}/common/pages/${slug}/alternates/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updatePage = ({ version = 'v1', slug, payload }) =>
  client
    .request({
      url: `${version}/common/pages/${slug}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateAlternatePage = ({ version = 'v1', slug, payload, uuid }) =>
  client
    .request({
      url: `${version}/common/pages/${slug}/alternates/${uuid}`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deletePage = ({ version = 'v1', slug }) =>
  client
    .request({
      url: `${version}/common/pages/${slug}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteAlternatePage = ({ version = 'v1', slug, alternateUuid }) =>
  client
    .request({
      url: `${version}/common/pages/${slug}/alternates/${alternateUuid}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const setPageOrdering = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/common/pages/set-ordering/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentsHash = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/sl_users/students-hash/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAddress = ({ addressId, version = 'v1' }) =>
  client
    .request({
      url: `${version}/common/addresses/${addressId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createAddress = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/common/addresses/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateAddress = ({ addressId, payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/common/addresses/${addressId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const uploadVideo = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/common/user-recorded-videos/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const uploadVideoMultipart = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/common/user-recorded-videos/upload`,
      method: RequestMethod.POST,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data: payload,
      timeout: 300000,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getVideo = ({ videoId, version = 'v1' }) =>
  client
    .request({
      url: `${version}/common/user-recorded-videos/${videoId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createMobileLink = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/common/video-mobile-link/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMobileLinks = ({ version = 'v1' }) =>
  client
    .request({
      url: `${version}/common/video-mobile-link/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMobileLink = ({ version = 'v1', hashId }) =>
  client
    .request({
      url: `${version}/common/video-mobile-link/${hashId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getUserRecordedVideoFileExists = ({ version = 'v1', videoId }) =>
  client
    .request({
      url: `${version}/common/user-recorded-videos/${videoId}/file-exists/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveEmailTemplates = ({ version = 'v1', name }) =>
  client
    .request({
      url: `${version}/common/email-templates/${name}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getUserIpAddress = ({ version = 'v1' }) =>
  client
    .request({
      url: `${version}/common/user-ip/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCountries = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/common/countries/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const submitFeedback = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/common/feedback/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {Object<{zipCode: string}> or Object<{latitude: number, longitude: number}>} payload
 * @response {Object<{latitude: number, longitude: number, id: number, zipCode: string}>}
 */
export const createUserLocation = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/common/locations/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);
